// native
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';

// service
import { GlobalsService } from '../core/services/globals.service';
import { AuthService } from '../core/services/auth.service';
import { UserService } from '../core/services/user.service';
import { CustomStyleService } from '../core/services/custom-style.service';
import { ToastService } from '../core/services/toast.service';
import { LegacyService } from '../core/services/legacy.service';
import { StylesService } from '../core/services/styles.service';
import { LocaleService } from '../core/services/locale.service';
import { CollectionsService } from '../core/services/collections.service';
import { MatcherService } from '../core/services/matcher.service';

// models
import { ConvertFromResult, ReformatPayload } from '../models';

@Component({
  selector: 'scs-options',
  templateUrl: './options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'scroll-wrapper scrollbar'
  }
})
export class OptionsComponent implements OnInit {

  isLoading: boolean = false;
  isWord: boolean = false;
  isWordDesktop: boolean = false;
  languages: { id: string; name: string; }[];
  selectedLanguage: string;

  constructor(
    private authService: AuthService,
    public globalsService: GlobalsService,
    public userService: UserService,
    public customStyleService: CustomStyleService,
    private toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef,
    private legacyService: LegacyService,
    private stylesService: StylesService,
    private localeService: LocaleService,
    private collectionService: CollectionsService,
    public matcherService: MatcherService
  ) { }

  ngOnInit() {
    this.isWord = !!this.globalsService?.appConfig?.convertFromEndnote;
    this.isWordDesktop =
      !!this.globalsService?.appConfig?.convertFromEndnote
      && (window === window.top);

    this.languages = this.localeService.getLocaleList();
    this.selectedLanguage = this.globalsService.language;
  }

  onWebLibraryClick() {
    window.open(
      this.globalsService.appConfig.environment.baseUrls.readcube,
      '_blank'
    );
  }

  onAccountClick() {
    window.open(
      `${this.globalsService.appConfig.environment.baseUrls.readcube}/account`,
      '_blank'
    );
  }

  onSupportClick() {
    window.open(
      this.globalsService.appConfig.environment.baseUrls.support,
      '_blank'
    );
  }

  onSignoutClick() {
    this.authService.signout();
  }

  openCustomStyleDialog() {
    this.customStyleService.isCustomStyleDialogOpen = true;
  }

  onConvertToPlainClick() {
    const confirmCallback = () => {
      this.isLoading = true;
      this.changeDetectorRef.markForCheck();

      this.globalsService.appConfig.convertToPlainText().then(() => {
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();

        this.toastService.openToast({
          text: 'Document is successfully converted to plain text.',
          type: 'success'
        });
      })
        .catch(err => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          this.toastService.showGenericError();
        });
    };

    this.toastService.openToast({
      text: `This action will convert all citations and the bibliography to plain text. After this is done, SmartCite will not be able to 
        recognize the references. Other objects inside the document will not be affected by this conversion. This action is irreversible in 
        SmartCite and it might be impossible to Undo in Word; make sure you make a copy of your document before proceeding.`,
      type: 'error',
      confirmButton: {
        text: 'Convert document',
        callback: confirmCallback
      },
      cancelButton: {
        text: 'Cancel',
        callback: () => { }
      },
      autoClose: false
    });
  }

  async onConvertFromEndnoteClick() {
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    try {
      let style = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();

      this.globalsService.appConfig.convertFromEndnote(style, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn)
        .then((result: ConvertFromResult) => {
          this.isLoading = false;
          this.globalsService.isSectionsModeOn = false;
          this.changeDetectorRef.markForCheck();

          result?.isConverted
            ? this.toastService.openToast({
              text: this.getConvertSuccessMessage(result, 'Endnote'),
              type: 'success',
              autoClose: false
            })
            : this.toastService.openToast({
              text: 'There are no Endnote citations in the document detected.',
              type: 'success'
            });
        })
        .catch(err => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          this.toastService.showGenericError();
        });
    } catch {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      this.toastService.showGenericError();
    }
  }

  async onConvertFromQuosaClick() {
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    try {
      let style = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();

      this.globalsService.appConfig.convertFromQuosa(style, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn)
        .then((result: ConvertFromResult) => {
          this.isLoading = false;
          this.globalsService.isSectionsModeOn = false;
          this.changeDetectorRef.markForCheck();

          result?.isConverted
            ? this.toastService.openToast({
              text: this.getConvertSuccessMessage(result, 'Quosa'),
              type: 'success',
              autoClose: false
            })
            : this.toastService.openToast({
              text: 'There are no Quosa citations in the document detected.',
              type: 'success'
            });
        })
        .catch(err => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          this.toastService.showGenericError();
        });
    } catch {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      this.toastService.showGenericError();
    }
  }

  // async onConvertLegacyClick() {
  //   const documentStyle = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();

  //   this.legacyService.convertLegacy(documentStyle, true);
  // }

  toggleAutoBibliographyUpdate() {
    this.globalsService.toggleAutoBibliographyUpdate();

    if (!this.globalsService.isBibliographyAutoUpdateOn && this.globalsService.isHyperlinksModeOn)
      this.globalsService.toggleHyperlinksMode();
  }

  async toggleHyperlinksMode() {
    this.isLoading = true;
    this.globalsService.toggleHyperlinksMode();

    if (this.globalsService.isHyperlinksModeOn && !this.globalsService.isBibliographyAutoUpdateOn)
      this.globalsService.toggleAutoBibliographyUpdate();

    try {
      let styleFilestring = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();
      await this.globalsService.appConfig.updateBibliography(styleFilestring, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn, this.globalsService.isSectionsModeOn);
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    } catch {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  async toggleSectionsMode() {
    this.isLoading = true;

    try {
      let styleFilestring = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();
      this.globalsService.appConfig.updateBibliography(styleFilestring, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn, !this.globalsService.isSectionsModeOn)
        .then((response: ReformatPayload | void) => {
          this.globalsService.isSectionsModeOn = !this.globalsService.isSectionsModeOn;
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();

          if ((<ReformatPayload>response)?.canceledByUser) {
            return;
          }

          if ((<ReformatPayload>response)?.numCslErrCits) {
            this.toastService.showCslError((<ReformatPayload>response).numCslErrCits);
            return;
          }

          this.toastService.openToast({
            text: 'Bibliography is successfully updated.',
            type: 'success'
          });
        })
        .catch(err => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();

          this.toastService.openToast({
            text: 'There was an error while updating bibliography.',
            type: 'error'
          });
        });
    } catch {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  onHyperlinkLearnMoreClick(event) {
    event.stopPropagation();
    window.open('https://support.papersapp.com/support/solutions/articles/30000046106', '_blank');
  }

  onSectionModeClick(event) {
    event.stopPropagation();
    window.open('https://support.papersapp.com/support/solutions/articles/30000046931-working-with-multiple-section-bibliographies-in-smartcite', '_blank');
  }

  onTutorialClick() {
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();
    this.globalsService.appConfig.openTutorial().then(() => {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }).catch(() => {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  onRefreshClick() {
    window.location.reload();
  }

  onLanguageChange(event) {
    this.isLoading = true;

    this.localeService.setLanguageAndLocale(event?.target?.value).then(async () => {
      let styleFilestring = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();
      this.globalsService.appConfig.updateBibliography(styleFilestring, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn, this.globalsService.isSectionsModeOn).then(() => {
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      }).catch(() => {
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      });
    }).catch(() => {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  private getConvertSuccessMessage(result: ConvertFromResult, app: string) {
    const collectionId = this.matcherService.unmatchedTargetLibraryId || this.collectionService.personalLibrary.id;
    const path = `library/${collectionId}/all?query=import_source:endnote AND import_by:smartcite`;
    const commonPart =
      `Check <a href="${this.globalsService.appConfig.environment.baseUrls.webapp}/${path}" target="_blank">
      here</a> to see all the ${app} converted references added to ${this.matcherService.unmatchedTargetLibraryId ? 'the shared' : ' your personal'} library.`;

    if (result?.addedCount && result?.existingCount)
      return `Document is successfully converted from ${app}. ${result.existingCount} 
              reference${result.existingCount > 1 ? 's' : ''} ${result.existingCount > 1 ? 'were' : 'was'} previously 
              imported and matched in Papers, while ${result.addedCount} new reference${result.addedCount > 1 ? 's' : ''} 
              ${result.addedCount > 1 ? 'were' : 'was'} added to ${this.matcherService.unmatchedTargetLibraryId ? 'the shared' : ' your personal'} library. 
              ${commonPart}`;

    if (result?.addedCount && !result?.existingCount)
      return `Document is successfully converted from ${app}. ${result.addedCount} new reference${result.addedCount > 1 ? 's' : ''} 
              ${result.addedCount > 1 ? 'were' : 'was'} added to ${this.matcherService.unmatchedTargetLibraryId ? 'the shared' : ' your personal'} library. 
              ${commonPart}`;

    if (!result?.addedCount && result?.existingCount)
      return `Document is successfully converted from ${app}. All references found in document were previously imported and matched in your Papers libraries.`;

    return `Document is successfully converted from ${app}.`;
  }
};
